import React, { useState } from 'react';
import { useAtom } from 'jotai';
import { Modal, Input, Select, Button, message, Radio } from 'antd';
import { 
  generatedScriptAtom, 
  isGeneratingScriptAtom,
  scriptGenerationErrorAtom,
  scriptTopicAtom,
  scriptToneAtom,
  scriptDurationAtom,
  isEditingScriptAtom,
  editedScriptAtom,
  regenerationFeedbackAtom,
  isRegeneratingAtom,
  resetScriptStateAtom
} from '../../atoms/aiAtoms';
import { AIScriptService, ScriptTone, TemplateCategory } from '../../services/aiScriptService';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { UpgradePrompt } from '../overlays/UpgradePrompt';
import './AIScriptGenerator.scss';

const { TextArea } = Input;
const { Option } = Select;

interface AIScriptGeneratorProps {
  visible: boolean;
  onClose: () => void;
  onUseScript: (script: string) => void;
  category: TemplateCategory;
  templateType: string;
  sceneTitle?: string;
  sceneContext?: string;
  defaultDuration?: number;
}

export const AIScriptGenerator: React.FC<AIScriptGeneratorProps> = ({
  visible,
  onClose,
  onUseScript,
  category,
  templateType,
  sceneTitle,
  sceneContext,
  defaultDuration = 60
}) => {
  const [generatedScript, setGeneratedScript] = useAtom(generatedScriptAtom);
  const [isGenerating, setIsGenerating] = useAtom(isGeneratingScriptAtom);
  const [error, setError] = useAtom(scriptGenerationErrorAtom);
  const [topic, setTopic] = useAtom(scriptTopicAtom);
  const [tone, setTone] = useAtom(scriptToneAtom);
  const [duration, setDuration] = useAtom(scriptDurationAtom);
  const [isEditing, setIsEditing] = useAtom(isEditingScriptAtom);
  const [editedScript, setEditedScript] = useAtom(editedScriptAtom);
  const [feedback, setFeedback] = useAtom(regenerationFeedbackAtom);
  const [isRegenerating, setIsRegenerating] = useAtom(isRegeneratingAtom);
  const [, resetState] = useAtom(resetScriptStateAtom);
  
  // Premium feature gating for AI script generation
  const { isPaid } = useFeatureAccess();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  React.useEffect(() => {
    if (visible) {
      setDuration(defaultDuration);
    }
  }, [visible, defaultDuration, setDuration]);

  const handleGenerate = async () => {
    // Gate AI generation for premium users only
    if (!isPaid) {
      setShowUpgradePrompt(true);
      return;
    }
    
    if (!topic.trim()) {
      message.warning('Please enter a topic for your script');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const result = await AIScriptService.generateScript({
        templateType,
        category,
        userTopic: topic,
        tone,
        duration,
        sceneContext,
        sceneTitle
      });

      setGeneratedScript(result);
      setEditedScript(result.script);
      message.success(`Script generated in ${result.metadata.generatedAt}ms!`);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to generate script';
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    if (!generatedScript) return;
    
    if (!isPaid) {
      setShowUpgradePrompt(true);
      return;
    }

    setIsRegenerating(true);
    setError(null);

    try {
      const result = await AIScriptService.regenerateScript(
        {
          templateType,
          category,
          userTopic: topic,
          tone,
          duration,
          sceneContext,
          sceneTitle
        },
        feedback
      );

      setGeneratedScript(result);
      setEditedScript(result.script);
      setFeedback('');
      message.success('Script regenerated!');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to regenerate script';
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleUseScript = () => {
    const scriptToUse = isEditing ? editedScript : generatedScript?.script;
    if (scriptToUse) {
      onUseScript(scriptToUse);
      onClose();
      message.success('Script applied to scene!');
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const scriptPreview = isEditing ? editedScript : generatedScript?.script || '';
  const estimatedTime = generatedScript 
    ? AIScriptService.estimateReadingTime(scriptPreview) 
    : 0;

  return (
    <Modal
      title="AI Script Generator"
      open={visible}
      onCancel={handleClose}
      width={800}
      footer={[
        <Button key="close" onClick={handleClose}>
          Cancel
        </Button>,
        generatedScript && (
          <Button 
            key="edit" 
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? 'Preview' : 'Edit Script'}
          </Button>
        ),
        generatedScript && (
          <Button
            key="use"
            type="primary"
            onClick={handleUseScript}
          >
            Use This Script
          </Button>
        )
      ]}
      className="ai-script-generator-modal"
    >
      <div className="ai-script-generator">
        {/* Input Section */}
        {!generatedScript && (
          <div className="input-section">
            <div className="form-group">
              <label>What's your video about?</label>
              <Input
                placeholder="E.g., Introduction to React Hooks"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                size="large"
                disabled={isGenerating}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Tone</label>
                <Radio.Group 
                  value={tone} 
                  onChange={(e) => setTone(e.target.value as ScriptTone)}
                  disabled={isGenerating}
                >
                  <Radio.Button value="professional">Professional</Radio.Button>
                  <Radio.Button value="casual">Casual</Radio.Button>
                  <Radio.Button value="energetic">Energetic</Radio.Button>
                </Radio.Group>
              </div>

              <div className="form-group">
                <label>Duration (seconds)</label>
                <Select
                  value={duration}
                  onChange={setDuration}
                  style={{ width: 150 }}
                  disabled={isGenerating}
                >
                  <Option value={30}>30s</Option>
                  <Option value={60}>60s</Option>
                  <Option value={90}>90s</Option>
                  <Option value={120}>2min</Option>
                  <Option value={180}>3min</Option>
                </Select>
              </div>
            </div>

            <Button
              type="primary"
              size="large"
              onClick={handleGenerate}
              loading={isGenerating}
              block
            >
              {isGenerating ? 'Generating...' : 'Generate Script'}
            </Button>

            {error && (
              <div className="error-message">{error}</div>
            )}
          </div>
        )}

        {/* Script Preview/Edit Section */}
        {generatedScript && (
          <div className="script-section">
            <div className="script-header">
              <div className="script-stats">
                <span className="stat">
                  <strong>{generatedScript.wordCount}</strong> words
                </span>
                <span className="stat">
                  <strong>~{estimatedTime}s</strong> estimated
                </span>
                <span className={`stat pace-indicator ${Math.abs(estimatedTime - duration) <= duration * 0.2 ? 'good' : 'warning'}`}>
                  {Math.abs(estimatedTime - duration) <= duration * 0.2 
                    ? '✓ Good length' 
                    : `⚠ ${estimatedTime > duration ? 'Too long' : 'Too short'}`}
                </span>
              </div>
            </div>

            {isEditing ? (
              <TextArea
                value={editedScript}
                onChange={(e) => setEditedScript(e.target.value)}
                rows={12}
                className="script-editor"
                placeholder="Edit your script here..."
              />
            ) : (
              <div className="script-preview">
                {scriptPreview}
              </div>
            )}

            {/* Regeneration Section */}
            <div className="regeneration-section">
              <TextArea
                placeholder="Not quite right? Tell me what to change... (e.g., 'Make it more casual' or 'Add an example')"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={2}
                disabled={isRegenerating}
              />
              <Button
                onClick={handleRegenerate}
                loading={isRegenerating}
                disabled={!feedback.trim()}
                style={{ marginTop: 8 }}
              >
                Regenerate with Feedback
              </Button>
            </div>
          </div>
        )}
      </div>
      
      <UpgradePrompt
        visible={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        feature="AI Script Generation"
        description="Upgrade to Pro to unlock AI-powered script generation. Create professional scripts for your scenes in seconds with the help of artificial intelligence."
      />
    </Modal>
  );
};
